import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { configService } from '../services/configService';
import { fetchPensionSetupSnapshot } from '../services/boardingService';
import { PROFESSION_CATALOG } from '../utils/professionCatalog';
import {
  countPensionCoreServiceTicks,
  ONBOARDING_CORE_TOTAL_DEFAULT,
  onboardingCoreTotal,
} from '../utils/pensionOnboarding';
import { hasRegistrationId } from '../utils/registrationValidation';
import {
  isOnboardingServiceStepDone,
  resolveOnboardingBranch,
  resolveOnboardingLane,
} from '../utils/signupOnboardingBranch';
import { isPensionTrack } from '../utils/signupActivitySession';

/**
 * Progression "core" du démarrage : uniquement Essentiel
 * (identifiant légal, adresse, 1er cran métier) + Important
 * (photo, description, téléphone). Le Bonus (Stripe, calendrier, 1er séjour)
 * est exclu du calcul sidebar.
 *
 * Source de vérité alignée sur StartupPage.tsx (essentialSteps + importantSteps).
 */
export interface OnboardingCoreProgress {
  corePercent: number;
  coreDone: number;
  coreTotal: number;
  coreComplete: boolean;
  loading: boolean;
}

export const ONBOARDING_CORE_PROGRESS_EVENT = 'onboarding:core-progress';

const userScopedKey = (proId?: number | string, suffix?: string) =>
  `onboarding:v1:user:${proId ?? 'anon'}:${suffix ?? ''}`;

/** Publie la progression core (StartupPage → sidebar) sans attendre un remount. */
export function publishOnboardingCoreProgress(
  proId: number | string | undefined,
  coreDone: number,
  coreTotal: number = ONBOARDING_CORE_TOTAL_DEFAULT
): void {
  if (proId == null) return;
  const total = Math.max(1, Math.round(coreTotal));
  const clamped = Math.max(0, Math.min(total, Math.round(coreDone)));
  try {
    localStorage.setItem(userScopedKey(proId, 'coreDone'), String(clamped));
    localStorage.setItem(userScopedKey(proId, 'coreTotal'), String(total));
  } catch {
    /* ignore */
  }
  try {
    window.dispatchEvent(
      new CustomEvent(ONBOARDING_CORE_PROGRESS_EVENT, {
        detail: { proId, coreDone: clamped, coreTotal: total },
      })
    );
  } catch {
    /* ignore */
  }
}

export function useOnboardingCoreProgress(enabled: boolean): OnboardingCoreProgress {
  const { user } = useAuth();
  const proId = user?.pro_id;
  const location = useLocation();
  const [coreDone, setCoreDone] = useState(0);
  const [coreTotal, setCoreTotal] = useState(ONBOARDING_CORE_TOTAL_DEFAULT);
  const [loading, setLoading] = useState(true);

  const applyDone = useCallback(
    (done: number, total: number = coreTotal) => {
      const nextTotal = Math.max(1, Math.round(total));
      const clamped = Math.max(0, Math.min(nextTotal, done));
      setCoreTotal(nextTotal);
      setCoreDone(clamped);
      if (proId == null) return;
      try {
        localStorage.setItem(userScopedKey(proId, 'coreDone'), String(clamped));
        localStorage.setItem(userScopedKey(proId, 'coreTotal'), String(nextTotal));
      } catch {
        /* ignore */
      }
    },
    [coreTotal, proId]
  );

  const fetch = useCallback(async () => {
    if (!enabled || !proId) {
      setLoading(false);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const [proRes, servRes] = await Promise.all([
        axios.get(configService.getFullApiUrl(`/get-pro/${proId}`), {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios
          .get(configService.getFullApiUrl(`/get-services/${proId}`), {
            headers: { Authorization: `Bearer ${token}` },
          })
          .catch(() => null),
      ]);

      const pro = proRes.data || {};
      const services = servRes?.data?.services || servRes?.data || [];
      const servicesCount = Array.isArray(services) ? services.length : 0;
      const professionIds = Array.isArray(pro.profession_ids) ? pro.profession_ids : [];
      const hint = resolveOnboardingBranch({
        session: null,
        professions: PROFESSION_CATALOG,
        professionIds,
      });
      const lane = resolveOnboardingLane({
        session: null,
        professions: PROFESSION_CATALOG,
        professionIds,
      });
      const boardingEnabled = Boolean(
        pro.is_boarding_enabled || user?.is_boarding_enabled
      );
      const total = onboardingCoreTotal(lane);

      let serviceTicks = 0;
      if (isPensionTrack(lane)) {
        const snap = await fetchPensionSetupSnapshot(proId, token, boardingEnabled);
        const pensionTicks = countPensionCoreServiceTicks({
          ...snap,
          isBoardingEnabled: boardingEnabled,
        });
        if (lane === 'stays') {
          serviceTicks = pensionTicks;
        } else {
          const catalogDone = isOnboardingServiceStepDone({
            hint,
            servicesCount,
            isBoardingEnabled: boardingEnabled,
          });
          serviceTicks = (catalogDone ? 1 : 0) + pensionTicks;
        }
      } else if (
        isOnboardingServiceStepDone({
          hint,
          servicesCount,
          isBoardingEnabled: boardingEnabled,
        })
      ) {
        serviceTicks = 1;
      }

      let done = 0;
      if (hasRegistrationId(pro)) done += 1;
      if (pro.address) done += 1;
      done += serviceTicks;
      if (pro.profile_photo_url && !String(pro.profile_photo_url).includes('default-profile')) {
        done += 1;
      }
      if (pro.description) done += 1;
      if (pro.phone || pro.email) done += 1;

      applyDone(done, total);
    } catch {
      try {
        const stored = Number(localStorage.getItem(userScopedKey(proId, 'coreDone')) || '0');
        const storedTotal = Number(
          localStorage.getItem(userScopedKey(proId, 'coreTotal')) ||
            ONBOARDING_CORE_TOTAL_DEFAULT
        );
        setCoreTotal(storedTotal || ONBOARDING_CORE_TOTAL_DEFAULT);
        setCoreDone(stored);
      } catch {
        /* ignore */
      }
    } finally {
      setLoading(false);
    }
  }, [enabled, proId, applyDone, user?.is_boarding_enabled]);

  useEffect(() => {
    fetch();
  }, [fetch, location.pathname]);

  useEffect(() => {
    if (!enabled) return;

    const onPublished = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          proId?: number | string;
          coreDone?: number;
          coreTotal?: number;
        }>
      ).detail;
      if (detail?.proId != null && proId != null && String(detail.proId) !== String(proId)) {
        return;
      }
      if (typeof detail?.coreDone === 'number') {
        applyDone(detail.coreDone, detail.coreTotal ?? coreTotal);
        return;
      }
      void fetch();
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') void fetch();
    };

    window.addEventListener(ONBOARDING_CORE_PROGRESS_EVENT, onPublished);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      window.removeEventListener(ONBOARDING_CORE_PROGRESS_EVENT, onPublished);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [enabled, proId, applyDone, fetch, coreTotal]);

  const corePercent = coreTotal > 0 ? Math.round((coreDone / coreTotal) * 100) : 0;

  return {
    corePercent,
    coreDone,
    coreTotal,
    coreComplete: coreDone >= coreTotal,
    loading,
  };
}
